import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveCart,
  selectActiveCartItems,
  selectCartTotals,
  selectIsLoading,
  selectActiveBusinessId,
  selectActiveOrderingTime,
  selectActiveRestaurant,
  selectIsCartDirty,
  selectCartState,
} from './cartSelectors';
import {
  initializeCart,
  resetCart,
  setActiveCart,
  setOrderingTimeCart,
  setState,
  incrementItemQuantity,
  decrementItemQuantity,
  addToCart,
  updateToCart,
  deleteItem,
  proceedToPaymentThunk,
  saveAndSyncCartThunk,
  applyGiftCardThunk,
  removeGiftCardThunk,
  addDealToCart,
  removeDealFromCart,
  checkDecrementPricingThunk,
  checkDeletePricingThunk,
  checkUpdatePricingThunk,
} from './cartSlice';
import { OrderingTime } from './cartTypes';
import { CartItem } from '../../(routes)/online-ordering/types';
import { AppDispatch } from '..';

export const useActiveCart = () => {
  const cart = useSelector(selectActiveCart);
  const items = useSelector(selectActiveCartItems);
  const totals = useSelector(selectCartTotals);
  const isLoading = useSelector(selectIsLoading);
  const activeBusinessId = useSelector(selectActiveBusinessId);
  const orderingTime = useSelector(selectActiveOrderingTime);
  const restaurant = useSelector(selectActiveRestaurant);
  const isDirty = useSelector(selectIsCartDirty);

  return {
    cart,
    items,
    totals,
    isLoading,
    activeBusinessId,
    orderingTime,
    restaurant,
    isDirty,
  };
};

export const useCartActions = () => {
  const dispatch = useDispatch<AppDispatch>();

  return useMemo(
    () => ({
      initializeCart: (businessId: number, restaurant: any) => {
        dispatch(initializeCart({ businessId, restaurant }));
      },

      setActiveCart: (businessId: number) => {
        dispatch(setActiveCart(businessId));
      },

      setOrderingTimeCart: (businessId: number, orderingTime: OrderingTime) => {
        dispatch(setOrderingTimeCart({ businessId, orderingTime }));
      },

      setState: (payload: any) => {
        dispatch(setState(payload));
      },

      addToCart: (item: CartItem | any) => {
        dispatch(addToCart({ item }));
      },

      updateToCart: (updatedItem: CartItem, oldCartItem: CartItem) => {
        dispatch(updateToCart({ updatedItem, oldCartItem }));
      },

      deleteItem: (item: CartItem, index: number, quantity?: number) => {
        dispatch(deleteItem({ item, index, quantity }));
      },

      incrementItemQuantity: (item: CartItem, index: number, quantity?: number) => {
        dispatch(incrementItemQuantity({ item, index, quantity }));
      },

      decrementItemQuantity: (item: CartItem, index: number, quantity?: number) => {
        dispatch(decrementItemQuantity({ item, index, quantity }));
      },

      addDealToCart: (dealPayload: any) => {
        dispatch(addDealToCart(dealPayload));
      },

      removeDealFromCart: (dealPayload: any) => {
        dispatch(removeDealFromCart(dealPayload));
      },

      reset: () => {
        dispatch(resetCart());
      },

      // Async Thunks
      proceedToPayment: async (payload: {
        businessId: number;
        tipAmount: number;
        tipPercentage: number;
        specialNotes: string;
        addcustomtip: boolean;
        scheduleConfiguration?: any;
        restaurantId: number;
        customerUsername: string;
        guestSessionId: string;
        orderType: string;
        orderSource: string;
      }) => {
        return dispatch(proceedToPaymentThunk(payload)).unwrap();
      },

      saveAndSyncCart: async () => {
        return dispatch(saveAndSyncCartThunk()).unwrap();
      },

      applyGiftCard: async (payload: { giftCardData: any; amountRedeemed: number }) => {
        return dispatch(applyGiftCardThunk(payload)).unwrap();
      },

      removeGiftCard: async () => {
        return dispatch(removeGiftCardThunk()).unwrap();
      },

      // Pricing Check Functions (return calculated price)
      checkDecrementPricing: async (id: number, selectedModifierGroups: any) => {
        return dispatch(checkDecrementPricingThunk({ id, selectedModifierGroups })).unwrap();
      },

      checkDeletePricing: async (id: number, itemIndex: number) => {
        return dispatch(checkDeletePricingThunk({ id, itemIndex })).unwrap();
      },

      checkUpdatePricing: async (updatedItem: any, oldCartItem: any) => {
        return dispatch(checkUpdatePricingThunk({ updatedItem, oldCartItem })).unwrap();
      },
    }),
    [dispatch]
  );
};

export const useInitializeOrRetrieveCart = () => {
  const dispatch = useDispatch();
  const cartState = useSelector(selectCartState);

  const initializeOrRetrieveCart = useCallback(
    (businessId: number, restaurant: any) => {
      const existingCart = cartState.carts[businessId];

      if (existingCart) {
        dispatch(setActiveCart(businessId));
        return { isNew: false, cart: existingCart };
      } else {
        dispatch(initializeCart({ businessId, restaurant }));
        return { isNew: true, cart: null };
      }
    },
    [dispatch, cartState]
  );

  return initializeOrRetrieveCart;
};
